import React from 'react';
import { 
  Timer, 
  ImageIcon, 
  Building2, 
  CreditCard, 
  Receipt, 
  Info, 
  Briefcase, 
  Zap, 
  CheckCircle2 
} from 'lucide-react';
import { UserProfile } from '../../types';

interface BusinessStepProps {
  formData: UserProfile;
  errors: Record<string, string>;
  isUploading: boolean;
  isNicheOpen: boolean;
  setIsNicheOpen: (v: boolean) => void;
  isSearchingProfessions: boolean;
  filteredApiProfessions: { name: string; niche: string; isSmartMatch?: boolean }[];
  onFieldChange: (field: string, value: any) => void;
  onLogoUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onProfessionSelect: (profession: { name: string; niche: string }) => void;
}

export default function BusinessStep({
  formData,
  errors,
  isUploading,
  isNicheOpen,
  setIsNicheOpen,
  isSearchingProfessions,
  filteredApiProfessions,
  onFieldChange,
  onLogoUpload,
  onProfessionSelect
}: BusinessStepProps) {
  return (
    <div className="space-y-4">
      {/* Logo Upload Section */}
      <div className="bg-zinc-50 border border-zinc-200 rounded-2xl p-4 flex items-center gap-4">
        <label className={`relative cursor-pointer group shrink-0 ${isUploading ? 'pointer-events-none' : ''}`}>
          <div className={`w-16 h-16 rounded-xl overflow-hidden bg-white border-2 border-dashed ${isUploading ? 'border-[#004B87] animate-pulse' : 'border-zinc-200'} flex items-center justify-center group-hover:border-[#004B87] transition-colors`}>
            {isUploading ? (
              <Timer className="h-6 w-6 text-[#004B87] animate-spin" />
            ) : (formData.logoBase64 || formData.logoUrl) ? (
              <img src={formData.logoBase64 || formData.logoUrl} alt="Logo" className="w-full h-full object-cover aspect-square" />
            ) : (
              <ImageIcon className="h-6 w-6 text-zinc-400 group-hover:text-[#004B87] transition-colors" />
            )}
          </div>
          <input type="file" accept="image/*" className="hidden" onChange={onLogoUpload} disabled={isUploading} />
        </label>
        <div>
          <h4 className="text-sm font-bold text-zinc-900 mb-1">Logotipo da Empresa</h4>
          <p className="text-xs text-zinc-500">Adicione seu logo para os orçamentos (Compactado automaticamente)</p>
        </div>
      </div>

      <div className="space-y-1">
        <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest ml-1">Nome da sua Marca / Empresa</label>
        <div className="relative">
          <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
          <input
            type="text"
            value={formData.businessName || ''}
            onChange={(e) => onFieldChange('businessName', e.target.value)}
            className={`w-full bg-white border ${errors.businessName ? 'border-rose-500' : 'border-zinc-100'} rounded-2xl py-4 pl-12 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all`}
            placeholder="Nome fantasia"
          />
        </div>
        {errors.businessName && <p className="text-[10px] text-rose-500 font-bold ml-1">{errors.businessName}</p>}
      </div>


      <div className="space-y-1">
        <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest ml-1">Tipo de Empresa</label>
        <div className="relative">
          <CreditCard className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
          <select
            value={formData.taxRegime || ''}
            onChange={(e) => onFieldChange('taxRegime', e.target.value)}
            className={`w-full bg-white border ${errors.taxRegime ? 'border-rose-500' : 'border-zinc-100'} rounded-2xl py-4 pl-12 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all appearance-none`}
          >
            <option value="">Selecione o tipo</option>
            <option value="Pessoa Física">Pessoa Física / Autônomo</option>
            <option value="MEI">MEI</option>
            <option value="Simples Nacional">Simples Nacional</option>
            <option value="Lucro Presumido / Real">Lucro Presumido / Real (Regime Geral)</option>
          </select>
        </div>
        
        {(formData.taxRegime === 'MEI' || formData.taxRegime === 'Simples Nacional') && (
          <div className="bg-zinc-50 border border-zinc-200 rounded-2xl p-4 space-y-3 mt-2">
            <div className="flex items-center gap-2 mb-1">
              <Receipt className="h-4 w-4 text-[#004B87]" />
              <p className="text-xs font-bold text-zinc-900 uppercase tracking-widest">Emissão de Notas</p>
            </div>
            <p className="text-[10px] text-zinc-500 leading-tight">Você emite todas as notas ou repassa para o escritório?</p>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer group">
                <input 
                  type="radio" 
                  checked={formData.taxOfficeRepass === false} 
                  onChange={() => onFieldChange('taxOfficeRepass', false)}
                  className="h-4 w-4 text-[#004B87] focus:ring-[#004B87]/20 border-zinc-200"
                />
                <span className={`text-xs transition-colors ${formData.taxOfficeRepass === false ? 'text-[#004B87] font-bold' : 'text-zinc-500'}`}>Eu mesmo emito</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer group">
                <input 
                  type="radio" 
                  checked={formData.taxOfficeRepass === true} 
                  onChange={() => onFieldChange('taxOfficeRepass', true)}
                  className="h-4 w-4 text-[#004B87] focus:ring-[#004B87]/20 border-zinc-200"
                />
                <span className={`text-xs transition-colors ${formData.taxOfficeRepass === true ? 'text-[#004B87] font-bold' : 'text-zinc-500'}`}>Repasso p/ Escritório</span>
              </label>
            </div>
          </div>
        )}

        {formData.taxRegime === 'MEI' && (
          <div className="bg-blue-50 border border-blue-100 p-3 rounded-xl flex gap-2 mt-2">
            <Info className="h-4 w-4 text-blue-600 shrink-0 mt-0.5" />
            <p className="text-[10px] text-blue-800 leading-tight">
              <strong>Lembrete MEI:</strong> O pagamento do seu DAS é uma despesa administrativa (inclua no Custo Fixo). O sistema não adicionará taxa extra no preço final do seu orçamento.
            </p>
          </div>
        )}

        {formData.taxRegime === 'Simples Nacional' && (
          <div className="space-y-3 mt-3">
            <div className="space-y-1">
              <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest ml-1">Alíquota do Simples (%)</label>
              <div className="relative">
                <Receipt className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
                <input
                  type="number"
                  inputMode="decimal"
                  min="0"
                  max="100"
                  step="0.01"
                  value={formData.taxPercentage || ''}
                  onChange={(e) => onFieldChange('taxPercentage', Number(e.target.value))}
                  className="w-full bg-white border border-zinc-100 rounded-2xl py-4 pl-12 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all font-mono font-bold"
                  placeholder="Ex: 6.0"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs text-zinc-400 font-bold">%</span>
              </div>
              <p className="text-[9px] text-zinc-500 ml-1">Essa porcentagem é calculada automaticamente sobre o valor final para proteger o seu lucro.</p>
            </div>

            {/* Transição da Reforma Tributária (CBS & IBS) */}
            <div className="bg-emerald-50/60 border border-emerald-200/90 rounded-2xl p-3.5 space-y-2.5">
              <div className="flex items-center gap-2">
                <Zap className="h-4 w-4 text-emerald-700" />
                <p className="text-xs font-bold text-emerald-950 uppercase tracking-wider">Reforma Tributária (CBS & IBS)</p>
              </div>
              <p className="text-[10px] text-emerald-800 leading-tight">
                Como deseja calcular seus orçamentos durante a transição 2026/2027?
              </p>
              <div className="space-y-2">
                <label className="flex items-start gap-2.5 p-2 rounded-xl bg-white border border-emerald-100 cursor-pointer">
                  <input
                    type="radio"
                    name="taxReformRegime"
                    checked={!formData.taxReformRegime || formData.taxReformRegime === 'simples_padrao'}
                    onChange={() => onFieldChange('taxReformRegime', 'simples_padrao')}
                    className="mt-0.5 text-emerald-600 focus:ring-emerald-500"
                  />
                  <div className="text-xs">
                    <strong className="text-zinc-900 block font-bold">Simples Padrão (DAS Unificado)</strong>
                    <span className="text-[10px] text-zinc-500">Imposto embutido no preço por dentro (ideal para vendas ao consumidor final B2C).</span>
                  </div>
                </label>

                <label className="flex items-start gap-2.5 p-2 rounded-xl bg-white border border-emerald-100 cursor-pointer">
                  <input
                    type="radio"
                    name="taxReformRegime"
                    checked={formData.taxReformRegime === 'simples_b2b_reform'}
                    onChange={() => onFieldChange('taxReformRegime', 'simples_b2b_reform')}
                    className="mt-0.5 text-emerald-600 focus:ring-emerald-500"
                  />
                  <div className="text-xs">
                    <strong className="text-zinc-900 block font-bold">Simples com IVA Dual (Destaque CBS/IBS)</strong>
                    <span className="text-[10px] text-zinc-500">Calculado por fora com créditos de insumos. Permite que seu cliente PJ abata o imposto como crédito no caixa dele.</span>
                  </div>
                </label>
              </div>
            </div>
          </div>
        )}

        {(formData.taxReformRegime === 'simples_b2b_reform' || formData.taxRegime === 'Lucro Presumido / Real') && (
          <div className="space-y-1 mt-3">
            <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest ml-1">Categoria de Alíquota na Reforma</label>
            <select
              value={formData.favoredRegimeCategory || 'standard'}
              onChange={(e) => onFieldChange('favoredRegimeCategory', e.target.value)}
              className="w-full bg-white border border-zinc-100 rounded-2xl py-3.5 px-4 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500/20 transition-all"
            >
              <option value="standard">Alíquota Padrão de Referência (~26,5%)</option>
              <option value="health_edu">Saúde, Educação ou Veterinária (~10,6% — Redução 60%)</option>
              <option value="liberal_pro">Profissionais Liberais Regulamentados (~18,55% — Redução 30%)</option>
              <option value="food_services">Bares, Restaurantes e Alimentação (~15,9% — Redução 40%)</option>
            </select>
          </div>
        )}
        {errors.taxRegime && <p className="text-[10px] text-rose-500 font-bold ml-1">{errors.taxRegime}</p>}
      </div>

      {/* Dados de Recebimento (PIX) */}
      <div className="bg-zinc-50 border border-zinc-200 rounded-2xl p-4 space-y-3 box-border w-full max-w-full">
        <div className="flex items-center gap-2 mb-1">
          <Building2 className="h-4 w-4 text-[#004B87]" />
          <p className="text-xs font-bold text-zinc-900 uppercase tracking-widest">Sua Chave PIX</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1">
            <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest ml-1">Tipo de Chave</label>
            <select
              value={formData.pixKeyType || ''}
              onChange={(e) => onFieldChange('pixKeyType', e.target.value)}
              className="w-full bg-white border border-zinc-100 rounded-xl py-2.5 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all appearance-none"
            >
              <option value="">Selecione</option>
              <option value="CPF">CPF</option>
              <option value="CNPJ">CNPJ</option>
              <option value="E-mail">E-mail</option>
              <option value="Telefone">Telefone</option>
              <option value="Chave Aleatória">Chave Aleatória</option>
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest ml-1">Chave PIX</label>
            <input
              type="text"
              value={formData.pixKey || ''}
              onChange={(e) => onFieldChange('pixKey', e.target.value)}
              className="w-full bg-white border border-zinc-100 rounded-xl py-2.5 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all"
              placeholder="000.000..."
            />
          </div>
        </div>
        <p className="text-[9px] text-zinc-500 leading-tight italic">Será usada nos orçamentos para facilitar o pagamento.</p>

        {/* Toggle de Exibição de PIX nos Orçamentos */}
        <div className="pt-2 border-t border-zinc-200/60">
          <label className="flex items-center gap-3 cursor-pointer group">
            <div className="relative flex items-center">
              <input
                type="checkbox"
                checked={formData.showPixKey || false}
                onChange={(e) => onFieldChange('showPixKey', e.target.checked)}
                className="peer h-5 w-5 appearance-none rounded-md border-2 border-zinc-200 checked:bg-[#004B87] checked:border-[#004B87] transition-all"
              />
              <CheckCircle2 className="absolute h-3 w-3 text-white opacity-0 peer-checked:opacity-100 left-1 transition-opacity" />
            </div>
            <span className="text-xs text-zinc-600 font-medium group-hover:text-zinc-900 transition-colors">
              Exibir dados do PIX nos orçamentos?
            </span>
          </label>
          <p className="text-[10px] text-zinc-400 mt-2 ml-8 leading-tight">
            Se desmarcado, a sua chave PIX ficará oculta nas propostas em PDF.
          </p>
        </div>
      </div>

      <div className="space-y-1">
        <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest ml-1">Qual sua profissão?</label>
        <div className="relative">
          <Briefcase className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400 pointer-events-none" />
          <input
            type="text"
            autoComplete="off"
            value={formData.profession || ''}
            onChange={(e) => {
              onFieldChange('profession', e.target.value);
              setIsNicheOpen(true);
            }}
            onFocus={() => setIsNicheOpen(true)}
            onBlur={() => setTimeout(() => setIsNicheOpen(false), 200)}
            placeholder="Escreva ou busque sua profissão"
            className={`w-full bg-white border ${errors.profession ? 'border-rose-500' : 'border-zinc-100'} rounded-2xl py-4 pl-12 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all`}
          />
          
          {isNicheOpen && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-zinc-100 rounded-xl shadow-xl overflow-hidden max-h-60 overflow-y-auto w-full z-50 animate-in zoom-in-95 duration-200">
              {isSearchingProfessions ? (
                <div className="px-4 py-3 flex items-center justify-center gap-2 text-zinc-400">
                  <Timer className="h-4 w-4 animate-spin" />
                  <span className="text-xs font-medium">Buscando na inteligência R-MAX...</span>
                </div>
              ) : filteredApiProfessions.length > 0 ? (
                filteredApiProfessions.map((p) => (
                  <div
                    key={p.name}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      onProfessionSelect(p);
                    }}
                    className={`px-4 py-3 cursor-pointer transition-all text-sm border-b border-zinc-50 last:border-none hover:bg-blue-50 flex items-center justify-between group ${
                      formData.profession === p.name ? 'bg-blue-50/50 text-[#004B87] font-bold' : 'text-zinc-600'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg transition-colors ${p.isSmartMatch ? 'bg-amber-100 text-amber-600' : 'bg-zinc-100 text-zinc-400 group-hover:bg-blue-100 group-hover:text-blue-600'}`}>
                        {p.isSmartMatch ? <Zap className="h-4 w-4" /> : <Briefcase className="h-4 w-4" />}
                      </div>
                      <div>
                        <div className="font-semibold">{p.name}</div>
                        <div className="text-[10px] text-zinc-400 uppercase tracking-widest">{p.niche}</div>
                      </div>
                    </div>
                    {p.isSmartMatch && (
                      <span className="bg-amber-100 text-amber-700 text-[8px] font-black uppercase px-2 py-0.5 rounded-full tracking-tighter">Identificado</span>
                    )}
                    {formData.profession === p.name && <CheckCircle2 className="h-4 w-4 text-[#004B87]" />}
                  </div>
                ))
              ) : formData.profession && formData.profession.length >= 2 ? (
                <div className="px-6 py-8 text-center space-y-2">
                  <div className="bg-zinc-50 w-12 h-12 rounded-full flex items-center justify-center mx-auto">
                    <Info className="h-6 w-6 text-zinc-300" />
                  </div>
                  <p className="text-xs text-zinc-400 italic">
                    Não encontramos uma profissão exata,<br/>mas você pode continuar digitando.
                  </p>
                </div>
              ) : null}
            </div>
          )}
        </div>
        {errors.niche && <p className="text-[10px] text-rose-500 font-bold ml-1">{errors.niche}</p>}
      </div>
    </div>
  );
}
